/**
 * Battle rules that are POLICY, not mechanics.
 *
 * ┌─────────────────────────────────────────────────────────────────┐
 * │ TRAINER_FLEE_ALLOWED — TESTING-PHASE AFFORDANCE.                │
 * │                                                                 │
 * │ The cartridge law is "No! There's no running from a TRAINER    │
 * │ battle!" — but during the playtest phase the user speed-tests   │
 * │ runs by fleeing everything (round-4 decision, 2026-08-02). A   │
 * │ flee-as-concession design was floated and RESCINDED pending    │
 * │ litigation — fleeing a trainer leaves the encounter OPEN, no   │
 * │ loss beats, no resolution.                                     │
 * │                                                                 │
 * │ FLIP TO false BEFORE THE MVP SHARE GATE (or litigate first).   │
 * │ Both branches are implemented and tested: false = the          │
 * │ cartridge refusal beat (log line, menu stays live, turn not    │
 * │ consumed). true = flee works and leaves a COHERENT OPEN world  │
 * │ — see fledTrainerFlagResets.                                   │
 * └─────────────────────────────────────────────────────────────────┘
 */
import { FLAGS } from '../scripts/flagRegistry';
import { RIVAL_BATTLE_REFS } from '../data/trainerParties';

export const TRAINER_FLEE_ALLOWED = true;

export const TRAINER_FLEE_REFUSAL_LINE =
  "No! There's no running from a TRAINER battle!";

/** Pure decision: what the RUN action does in this battle. */
export function fleeOutcome(
  trainerScriptRef: string | undefined,
  fleeAllowed: boolean,
): 'flee' | 'refuse' {
  if (!trainerScriptRef) return 'flee'; // wild — always escapable
  return fleeAllowed ? 'flee' : 'refuse';
}

/** Flags runFromBattle must CLEAR when the player flees the given
 *  trainer, so the world stays coherent and OPEN (flee ≠ loss —
 *  concession was rescinded pending litigation):
 *
 *  Generic trainers (Liam, bug catchers, Brock) need nothing — they're
 *  never hidden, defeatedTrainers is victory-only, and both the LOS
 *  cone and the talk-to-battle branch re-engage undefeated refs. The
 *  RIVAL is the special case: his pre-battle cinematic sets
 *  RIVAL_DEFEATED_PALLET future-tense (the handler's self-gate) and
 *  hides his static NPC (HIDE_RIVAL_IN_LAB) before the walker takes
 *  over. A flee without these resets SAVED a soft-lock: empty lab,
 *  encounter unre-triggerable (the round-4 bug). Clearing both puts
 *  him back on the post-flee WAKE — WorldScene.repositionFledRivalNpc
 *  moves the map-authored entry to VAR_RIVAL_LAST_TILE first (round-5:
 *  without that he mounted at his home tile beside Oak), then
 *  refreshNpcVisibility mounts him mid-challenge where the player
 *  left him. Row-8 re-arms; the state-aware tap taunt works because
 *  the NPC is simply visible again at POST_PICK. */
export function fledTrainerFlagResets(trainerScriptRef: string): string[] {
  if (RIVAL_BATTLE_REFS.has(trainerScriptRef)) {
    return [FLAGS.RIVAL_DEFEATED_PALLET, FLAGS.HIDE_RIVAL_IN_LAB];
  }
  return [];
}

/** Win/loss channel from BattleScene to the rival post-battle
 *  cinematic: 'win' → "WHAT? Unbelievable!" + "Smell you later!";
 *  'loss' → the gloat variant. Written next to RIVAL_BATTLE_RESOLVED,
 *  cleared by the cinematic at handler-end. */
export const VAR_RIVAL_BATTLE_OUTCOME = 'VAR_RIVAL_BATTLE_OUTCOME';

/** Cartridge law (round-4 user ruling): the lab rival battle is the
 *  game's one SPARRING match — permadeath starts when the world
 *  opens. A party wipe against the rival must NOT end the run: the
 *  starter survives fully healed (the blackout-at-Mom's equivalent),
 *  nothing is recorded in the career graveyard, and the encounter
 *  resolves exactly like a win (state → SEALED) with the gloat
 *  dialogue variant. Every OTHER trainer (Liam, bug catchers, Brock)
 *  stays fully lethal. 'draw' counts as a loss here — the invariant
 *  is that only FLEE leaves the encounter open. */
export function isRivalSparringLoss(
  trainerScriptRef: string | undefined,
  winner: 'p1' | 'p2' | 'draw',
): boolean {
  return (
    winner !== 'p1' &&
    !!trainerScriptRef &&
    RIVAL_BATTLE_REFS.has(trainerScriptRef)
  );
}
